/**
 * isNew: assigns a 'new' tag
 * highlight: floats the integration to the top of the list
 * weight: sets the order in the highlight list, lower is first
 * total: number of related connections e.g mysql can have 3
 * enabledStatus: displayed for enabled "featured"
 * hideFromFeatured: never display as "featured" integration - used for overlapping ("Typeform" / "Forms") or alias-only ("Facebook Ads" / "Instagram Ads") integrations
 */
export type StaticIntegration = {
  id: string;
  forceOrder: number;
  imageSrc: string;
  name: string;
  overview: string;
  category: string;
  description: string;
  parsedUseCases: string[];
  enabled: boolean;
  beta: boolean;
  type: IntegrationType;
  path: string;
  homepage: string;
  enabledStatus?: string;
  subtitle?: string;
  audienceSync?: boolean;
  dynamicSegment?: string;
  dataFlow?: 'in' | 'out';
  isNew?: boolean;
  highlight?: boolean;
  hideFromFeatured?: boolean;
  total?: number;
  weight?: number;
  publicType: IntegrationType.connectPartner | IntegrationType.nonExistent | IntegrationType.existing;
};

export enum IntegrationType {
  experiment = 'Experiment',
  native = 'Native',
  partnerBuilt = 'Partner-built',
  zapier = 'Zapier',
  nativeDataOut = 'NativeDataOut',
  nonExistent = 'Non-existent',
  connectPartner = 'Connect-partner',
  existing = 'Existing', // used only for UI purposes to filter out: Existing Integrations
}

export enum IntegrationCategory {
  advertising = 'Advertising',
  analytics = 'Analytics',
  api = 'API',
  databases = 'Databases',
  dataManagement = 'Data management',
  leadGeneration = 'Lead generation',
  MessagingChannelTypes = 'Message channels',
  surveys = 'Surveys',
}

export enum IntegrationSyncEnabledStatusMessage {
  exporting = 'Exporting Customer.io data to a bucket',
  paused = 'Sync is paused',
}

export enum DataFlow {
  in = 'in',
  out = 'out',
}

export const FEATURE_FLAGGED_INTEGRATIONS: Record<string, string> = {
  cdp_out: 'cdp_enabled',
};

let integrationSlugs = [
  'customerioAPI',
  'adwords',
  'youtube',
  'googleAnalytics',
  'facebook',
  'facebookLeadAds',
  'instagram',
  'typeform',
  'jotform',
  'segmentIn',
  'segmentOut',
  'twilio',
  'slack',
  'mparticleOut',
  'email',
  'reportingWebhooks',
  'webhooks',
  'mobile',
  'amazonS3',
  'gcs',
  'yandex',
  'snowflakeIn',
  'snowflakeOut',
  'sqlserver',
  'bigQueryIn',
  'bigQueryOut',
  'redshiftIn',
  'redshiftOut',
  'mysql',
  'postgres',
  'customForms',
  'azureBlob',
  'cdpOut',
  'cdpIn',
] as const;
export type IntegrationSlug = (typeof integrationSlugs)[number];

export const IntegrationIDs = Object.freeze<Record<IntegrationSlug, string>>({
  customerioAPI: 'CIO',
  adwords: 'adwords',
  youtube: 'YouTube_Ads',
  googleAnalytics: 'Google_Analytics',
  facebook: 'facebook',
  facebookLeadAds: 'Facebook_Lead_Ads',
  instagram: 'Instagram',
  typeform: 'Typeform_native',
  jotform: 'Jotform_native',
  segmentIn: 'Segment_Destination',
  segmentOut: 'Segment_Source',
  twilio: 'Twilio',
  slack: 'Slack',
  mparticleOut: 'Mparticle_Source',
  email: 'email',
  reportingWebhooks: 'reporting_webhooks',
  webhooks: 'Webhooks',
  mobile: 'mobile',
  amazonS3: 'amazons3',
  gcs: 'gcs',
  yandex: 'yandex',
  snowflakeIn: 'snowflake-in',
  snowflakeOut: 'snowflake-out',
  sqlserver: 'sqlserver',
  bigQueryIn: 'bigquery-in',
  bigQueryOut: 'bigquery-out',
  redshiftIn: 'redshift-in',
  redshiftOut: 'redshift-out',
  mysql: 'mysql',
  postgres: 'postgres',
  customForms: 'Custom_forms',
  azureBlob: 'azure_blob',
  cdpOut: 'cdp_out',
  cdpIn: 'cdp_in',
});

export type IntegrationKeys = (typeof IntegrationIDs)[keyof typeof IntegrationIDs];

export const IntegrationNames = Object.freeze<Record<IntegrationSlug, string>>({
  customerioAPI: 'Customer.io API',
  adwords: 'Google Ads',
  youtube: 'YouTube Ads',
  googleAnalytics: 'Google Analytics',
  facebook: 'Facebook Ads',
  facebookLeadAds: 'Facebook Lead Ads',
  instagram: 'Instagram Ads',
  typeform: 'Typeform',
  jotform: 'Jotform',
  segmentIn: 'Segment',
  segmentOut: 'Segment',
  twilio: 'Twilio SMS',
  slack: 'Slack',
  mparticleOut: 'mParticle',
  email: 'Email',
  reportingWebhooks: 'Reporting webhooks',
  webhooks: 'Webhooks',
  mobile: 'Mobile Push',
  amazonS3: 'Amazon S3',
  gcs: 'Google Cloud Storage',
  yandex: 'Yandex',
  snowflakeIn: 'Snowflake',
  snowflakeOut: 'Snowflake',
  sqlserver: 'Microsoft SQL Server',
  bigQueryIn: 'Google BigQuery',
  bigQueryOut: 'Google BigQuery',
  redshiftIn: 'Amazon Redshift',
  redshiftOut: 'Amazon Redshift',
  mysql: 'MySQL',
  postgres: 'PostgreSQL',
  customForms: 'Forms',
  azureBlob: 'Azure Blob Storage',
  cdpOut: 'Customer.io Data Pipelines',
  cdpIn: 'Customer.io Data Pipelines',
});

export default Object.freeze<Record<string, StaticIntegration>>({
  adwords: {
    id: IntegrationIDs.adwords,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/adwords.svg', import.meta.url).toString(),
    name: IntegrationNames.adwords,
    overview: 'Sync segments to Google Ad Audiences for retargeting on Google',
    category: IntegrationCategory.advertising,
    description: 'Google Ads connect your message to the right audience',
    parsedUseCases: [
      'Sync Customer.io segments to Google Ad Audiences',
      'Create Google Similar Audiences based on these Ad Audiences',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    audienceSync: true,
    path: 'integrations-public.type',
    dynamicSegment: 'adwords',
    homepage: 'https://ads.google.com',
    dataFlow: DataFlow.out,
  },
  youtube: {
    id: IntegrationIDs.youtube,
    forceOrder: 0,
    imageSrc: 'https://cdn.zapier.com/storage/services/7633e61620664fb3a71911729ebce5ea.128x128.png',
    name: IntegrationNames.youtube,
    overview: 'Sync segments to Google Ad Audiences for retargeting on YouTube',
    category: IntegrationCategory.advertising,
    description: 'YouTube Ads connect your message to the right audience.',
    parsedUseCases: [
      'Sync Customer.io segments to Google Ad Audiences, then retarget on YouTube',
      'Create Google Similar Audiences based on these Ad Audiences',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    audienceSync: true,
    path: 'integrations-public.type',
    dynamicSegment: 'YouTube_Ads',
    homepage: 'https://youtube.com',
    dataFlow: DataFlow.out,
    hideFromFeatured: true,
  },
  googleAnalytics: {
    id: IntegrationIDs.googleAnalytics,
    forceOrder: 0,
    imageSrc: 'https://cdn.zapier.com/storage/services/8e760f76ff742d22fb037a3ed49e009d.128x128.png',
    name: IntegrationNames.googleAnalytics,
    overview: 'Send UTM Parameters along with your Customer.io messages',
    category: IntegrationCategory.analytics,
    description: 'Google Analytics is an industry standard freemium web and mobile analytics package.',
    parsedUseCases: ['Automatically append UTM parameters to links in Customer.io campaigns'],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Google_Analytics',
    homepage: 'https://analytics.google.com',
    dataFlow: DataFlow.out,
  },
  facebook: {
    id: IntegrationIDs.facebook,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/facebook.svg', import.meta.url).toString(),
    name: IntegrationNames.facebook,
    overview: 'Sync segments to Facebook Custom Audiences for retargeting on Facebook',
    category: IntegrationCategory.advertising,
    description: 'Facebook Ads connect your message to the right audience.',
    parsedUseCases: [
      'Sync Customer.io segments to Facebook Custom Audiences',
      'Create Facebook Lookalike Audiences based on these Custom Audiences',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    audienceSync: true,
    path: 'integrations-public.type',
    dynamicSegment: 'facebook',
    homepage: 'https://facebook.com',
    dataFlow: DataFlow.out,
  },
  instagram: {
    id: IntegrationIDs.instagram,
    forceOrder: 0,
    imageSrc: 'https://cdn.zapier.com/storage/developer/30129480e943e5b3e334394168ae16a2.128x128.png',
    name: IntegrationNames.instagram,
    overview: 'Sync segments to Facebook Custom Audiences for retargeting on Instagram',
    category: IntegrationCategory.advertising,
    description: 'Instagram Ads connect your message to the right audience.',
    parsedUseCases: [
      'Sync Customer.io segments to Facebook Custom Audiences, then retarget on Instagram',
      'Create Facebook Lookalike Audiences based on these Custom Audiences',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    audienceSync: true,
    path: 'integrations-public.type',
    dynamicSegment: 'Instagram',
    homepage: 'https://instagram.com',
    dataFlow: DataFlow.out,
    hideFromFeatured: true,
  },
  facebookLeadAds: {
    id: IntegrationIDs.facebookLeadAds,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/facebook.svg', import.meta.url).toString(),
    name: IntegrationNames.facebookLeadAds,
    overview: 'Connect your Facebook Lead Ads forms to start messaging your leads',
    category: IntegrationCategory.leadGeneration,
    description:
      'Facebook Lead Ads makes signing up for business information easy for people and more valuable for businesses.',
    parsedUseCases: [
      'Add Customer.io profiles from Facebook Lead Ad submissions',
      'Trigger Customer.io campaigns from Facebook Lead Ad submissions',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Facebook_Lead_Ads',
    homepage: 'https://facebook.com',
    dataFlow: DataFlow.in,
    hideFromFeatured: true,
  },
  typeform: {
    id: IntegrationIDs.typeform,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/typeform.svg', import.meta.url).toString(),
    name: IntegrationNames.typeform,
    overview: 'Create profiles and trigger campaigns from Typeform',
    category: IntegrationCategory.surveys,
    description: 'Typeform helps you run surveys, questionnaires, forms, contests and more across all devices.',
    parsedUseCases: ['Create Customer.io profiles and trigger campaigns when a new Typeform response is recorded.'],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Typeform',
    homepage: 'https://typeform.com',
    dataFlow: DataFlow.in,
    hideFromFeatured: true,
  },
  jotform: {
    id: IntegrationIDs.jotform,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/jotform.svg', import.meta.url).toString(),
    name: IntegrationNames.jotform,
    overview: 'Create profiles and trigger campaigns from Jotform',
    category: IntegrationCategory.surveys,
    description: 'Jotform lets you build forms fast, with powerful widgets and a responsive editor that works offline.',
    parsedUseCases: ['Create Customer.io profiles or events when a Jotform is submitted'],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Jotform',
    homepage: 'https://jotform.com',
    dataFlow: DataFlow.in,
    hideFromFeatured: true,
  },
  segmentOut: {
    id: IntegrationIDs.segmentOut,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/segment.svg', import.meta.url).toString(),
    name: IntegrationNames.segmentOut,
    subtitle: 'Source',
    overview: 'Send message activity back to Segment.com',
    category: IntegrationCategory.dataManagement,
    description: 'Segment allows you to unify customer data and analytics in one place.',
    parsedUseCases: [
      'Send message opens, clicks, unsubscribes, failures, bounces, and spam flags from Customer.io to Segment',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Segment_Source',
    homepage: 'https://segment.io',
    dataFlow: DataFlow.out,
  },
  twilio: {
    id: IntegrationIDs.twilio,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/integrations/twilio.svg', import.meta.url).toString(),
    name: IntegrationNames.twilio,
    overview: 'Send SMS messages in Customer.io',
    category: IntegrationCategory.MessagingChannelTypes,
    description:
      "Customer.io's Twilio SMS Action allows you to easily communicate with your customers through Customer.io.",
    parsedUseCases: ['Send SMS messages in Customer.io campaigns'],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Twilio',
    homepage: 'https://twilio.com',
  },
  slack: {
    id: IntegrationIDs.slack,
    forceOrder: 0,
    imageSrc: new URL('cio/assets/images/actions/slack.svg', import.meta.url).toString(),
    name: IntegrationNames.slack,
    overview: 'Send Slack messages in Customer.io',
    category: IntegrationCategory.MessagingChannelTypes,
    description:
      "Customer.io's Slack Action helps your teams work better together by passing information directly into Slack triggered by user behavior in your app, all customized to your needs.",
    parsedUseCases: ['Send Slack messages to individuals or channels through Customer.io campaigns'],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Slack',
    homepage: 'https://slack.com',
  },
  mparticleOut: {
    id: IntegrationIDs.mparticleOut,
    forceOrder: 0,
    imageSrc: 'https://logo.clearbit.com/mparticle.com',
    name: IntegrationNames.mparticleOut,
    subtitle: 'Source',
    overview: 'Send message activity back to mParticle',
    category: IntegrationCategory.dataManagement,
    description:
      'mParticle is a customer data platform, allowing marketers to integrate and orchestrate their entire growth stack so they win in key moments of the customer journey.',
    parsedUseCases: [
      'Sync mParticle Audiences to Customer.io People and Attributes',
      'Forward Events from mParticle to Customer.io',
      'Documentation for mParticle Audiences: http://docs.mparticle.com/integrations/customerio/audience',
      'Documentation for mParticle Events: http://docs.mparticle.com/integrations/customerio/event]',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'Mparticle_Source',
    homepage: 'https://mparticle.com',
    dataFlow: DataFlow.out,
  },
  cdpOut: {
    id: IntegrationIDs.cdpOut,
    forceOrder: 0,
    imageSrc: 'https://unityassets.customer.io/images/customerio-data-pipelines-b2474de2ebc57fb2d548cd9be8c1858d.svg',
    name: IntegrationNames.cdpOut,
    overview: 'Send message activity to the Customer.io Data Pipelines',
    category: IntegrationCategory.dataManagement,
    description: 'Our CDP allows you to unify customer data and analytics in one place.',
    parsedUseCases: [
      'Send message opens, clicks, unsubscribes, failures, bounces, and spam flags from Customer.io to our CDP',
    ],
    enabled: false,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'integrations-public.type',
    dynamicSegment: 'cdp_out',
    homepage: 'https://customer.io',
    dataFlow: DataFlow.out,
  },
});
